<template>
  <div>
    <div class="h-screen flex">
      <!-- Left Panel: Emoji Grid -->
      <div
        @scroll="handleScroll"
        ref="scrollContainer"
        class="flex-none w-1/3 overflow-y-scroll bg-gray-000"
      >
        <div class="grid grid-cols-3 sm:grid-cols-6 gap-4 p-4">
          <button
            v-for="emoji in visibleEmojis"
            :key="emoji"
            class="flex items-center justify-center cursor-pointer transform transition-transform duration-150 ease-in-out hover:scale-110"
            @click="setEmojiOne(emoji)"
          >
            <div
              class="bg-white transition-all duration-150 ease-in-out p-1 pb-0 transform hover:scale-110 sm:p-3 sm:pb-2 sm:pb-2 items-center text-xl sm:text-4xl justify-center rounded-xl shadow-lg"
              :class="{
                'border-2 sm:border-4 bg-sky-100 border-sky-300': one == emoji,
              }"
            >
              {{ emoji }}
            </div>
          </button>
        </div>
      </div>

      <div
        class="flex-1 p-4 text-center flex flex-col items-center justify-between h-full"
      >
        <a href="/" alt="Emoji Combiner" title="Emoji Combiner">
          <h1
            class="font-bold mt-2 text-xl sm:text-3xl bg-[radial-gradient(ellipse_at_right,_var(--tw-gradient-stops))] from-yellow-500 via-red-500 to-pink-500 text-transparent bg-clip-text"
          >
            Emoji Combiner
          </h1>
        </a>
        <h2 class="mt-1 text-sm sm:text-base text-gray-600">
          Choose 2 emojis to mix, and see their combination!
        </h2>

        <!-- Center Section: Combined Emoji Display -->
        <div class="flex-grow flex items-center justify-center w-full">
          <!-- Display your combined emoji image here -->
          <img
            v-if="combinedEmojiImage"
            :src="combinedEmojiImage"
            alt="Combined Emoji"
            class="w-2/3 h-2/3 object-contain"
          />
        </div>
        <!-- Button Group -->
        <div
          v-if="combinedEmojiImage"
          class="sm:flex space-y-3 text-sm sm:text-base sm:space-y-0 sm:space-x-4 mt-2"
        >
          <button
            @click="shareToTwitter"
            class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            <i class="fa fa-solid fa-share text-white"></i> Share
          </button>

          <button
            @click="downloadImage"
            class="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
          >
            <i
              class="hidden sm:inline-block fa fa-solid fa-download text-white"
            ></i>
            Download
          </button>

          <button
            @click="copyToClipboard"
            class="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            <i class="fa fa-solid fa-copy text-white"></i>
            Copy
          </button>
        </div>
      </div>

      <!-- Right Column -->
      <div
        @scroll="handleScrollRight"
        ref="scrollContainerRight"
        class="flex-none w-1/3 bg-gray-000 overflow-y-scroll"
      >
        <div class="grid grid-cols-3 sm:grid-cols-6 gap-4 p-4">
          <button
            v-for="emoji in visibleEmojisRight"
            :key="emoji"
            class="flex items-center justify-center cursor-pointer"
            @click="setEmojiTwo(emoji)"
          >
            <div
              class="bg-white transition-all duration-150 ease-in-out p-1 pb-0 transform hover:scale-110 sm:p-3 sm:pb-2 sm:pb-2 items-center text-xl sm:text-4xl justify-center rounded-xl shadow-lg"
              :class="{
                'border-2 sm:border-4 bg-red-100 border-red-300': two == emoji,
              }"
            >
              {{ emoji }}
            </div>
          </button>
        </div>
      </div>
    </div>

    <!-- TEXT HERE -->
    <div class="container mx-auto p-8">
      <h1 class="text-4xl font-bold mb-5">Your Ultimate Emoji Playground</h1>

      <p class="text-lg mb-4">
        Your emoji game is about to change forever. Introducing
        <strong class="font-semibold">Emoji Combiner</strong>, a revolutionary
        way to make emojis your own. Say goodbye to the standard set of emojis
        and say hello to an infinite realm of possibilities.
      </p>

      <h2 class="text-3xl font-semibold mb-2">What is Emoji Combiner?</h2>
      <p class="text-lg mb-6">
        In simple terms, Emoji Combiner is a cutting-edge platform that allows
        you to combine any two emojis to form an entirely new emoji. This is not
        just an application; it’s an ecosystem that empowers you to express
        yourself in unique ways.
      </p>

      <h2 class="text-3xl font-semibold mb-2">Why Combine Emojis?</h2>
      <p class="text-lg mb-6">
        Why send just a smiley or just a heart, when you can send a heart that
        smiles, a cat that dances, or a ghost that laughs? The combinations are
        only limited by your creativity. Consider Emoji Combiner as your emoji
        lab, where you're the scientist who experiments and discovers new
        elements of fun and expression.
      </p>

      <h2 class="text-3xl font-semibold mb-2">
        The Story Behind Emoji Combiner
      </h2>
      <p class="text-lg mb-6">
        What started as a fun project quickly gained momentum as more and more
        people realized the potential of combining emojis. We have seen our
        users create emojis that perfectly express complex emotions, something
        that wouldn’t have been possible with just the standard set of emojis.
      </p>

      <h2 class="text-3xl font-semibold mb-2">Features Galore</h2>
      <ul class="list-disc list-inside text-lg mb-6">
        <li class="mb-3">
          <strong class="font-semibold">Downloadable Emojis:</strong> Your
          combined emoji is yours to keep. Download it to your device and use it
          however you like.
        </li>
        <li class="mb-3">
          <strong class="font-semibold">Clipboard Copy:</strong> No need to
          download if you want to use it instantly, just copy it to your
          clipboard.
        </li>
        <li class="mb-3">
          <strong class="font-semibold">Social Sharing:</strong> Share your
          unique creations directly to your social media channels and let your
          friends in on the fun.
        </li>
      </ul>

      <h2 class="text-3xl font-semibold mb-2">How's it work?</h2>
      <p class="text-lg mb-6">
        Emoji Combiner utilizes the powerful Google Emoji Kitchen to blend your
        emojis. Google Emoji Kitchen started as a feature of Gboard, Google's
        keyboard, but it quickly gained recognition for its unique ability to
        mash up two emojis to create new, compound emojis.
      </p>

      <h2 class="text-3xl font-semibold mb-2">
        How Google Emoji Kitchen Changed the Game
      </h2>
      <p class="text-lg mb-6">
        While it started as a Google project, Emoji Kitchen quickly caught the
        attention of the creative minds of the world. Artists, social media
        influencers, and ordinary people took it as a platform to express
        complex ideas, jokes, and emotions in a unique way. Emoji Combiner takes
        that freedom and creativity to the next level.
      </p>

      <h2 class="text-3xl font-semibold mb-2">A Canvas for Your Creativity</h2>
      <p class="text-lg mb-6">
        Imagine having a blank canvas and a set of colors, only here, your
        colors are your favorite emojis, and the canvas is your chat window.
        Imagine the joy of being able to use an emoji that no one else has, an
        emoji that you created. This is not just about being different; it's
        about being you. It's about expressing yourself in a way that no one
        else can. And that's what Emoji Combiner aims to offer.
      </p>

      <h2 class="text-3xl font-semibold mb-2">
        Get Started with Emoji Combiner Today
      </h2>
      <p class="text-lg mb-6">
        The power to create, to express, and to enjoy is now in your hands. All
        you need to do is pick two emojis, and let Emoji Combiner do its magic.
        With an intuitive interface, endless options, and instant sharing
        capabilities, Emoji Combiner is the tool you didn't know you needed. So
        why wait? Dive into the world of infinite possibilities and express
        yourself like never before. Get started with Emoji Combiner today!
      </p>

      <p class="text-lg mb-4">
        Thank you for being a part of this exciting journey. Together, let's
        redefine how we communicate, one emoji at a time.
      </p>
      <p class="text-lg mb-4">Happy Combining! 🎉🥳</p>
    </div>
  </div>
</template>

<script>
import getEmojiMixUrl from "emoji-mixer";

export default {
  name: "Combiner",
  data() {
    return {
      one: null,
      two: null,
      combinedEmojiImage: null,
      visibleCount: 60,
      visibleCountRight: 60,
      emojis: Array.from(
        "😀😃😄😁😆🥹😅😂🤣😍😌😉🙃🙂😇😊🥲🥰😘😗😙😚😋😛😝😜😏🥳🤩🥸😎🤓🧐🤨🤪😒😞😔😟😕🙁😣😖🤬😡😠😤😭😢🥺😩😫🤯😳🥵🥶😨😰😥🫠🤫🫡🫢🤭🫣🤔🤗😓🤥😶🫥😐🫤😑😬🙄🤤😴🥱😲😮😧😦😵🤐🥴🤢🤮🤧😷🤒💩🤡👺👹👿😈🤠🤑🤕👻💀👽👾🤖🎃😺😸🤲🫶😾😿🙀😽😼😻😹👐🙌👏🤝👍👎✊🤛👊🤏🤌👌🤘🤟🫰🤜🫳🫴👈👉👆👇✋🤚🖕🦾💪🫱🫲🤙👋🖖🖐🙏🫵🦶🦵🦿💄💋👄👀👁👣👃🦻👂👅🦷🫦🫀🫁🧠🗣👤👥🫂🪢🧶🧵🪡🧥🥼🦺👚👘🩱👙👗👔🩳🩲👖👕🥻🩴🥿👠👡👢👞👟🥾🪖⛑👒🎓🧢🎩🧣🧤🧦👑💍👝👛👜💼🎒🧳👓🌂🥽🕶🧡💛💚💙💜🤎🖤🤍💖💗💓💞💕🐶🐱🐭🐹🐰🦊🐻🐼🐵🐸🐽🐷🐮🦁🐯🐨🙉🙊🐒🐔🐧🐦🐤🐣🐥🐝🦄🐴🐗🐺🦇🦉🦅🦆🪱🐛🦋🐌🐞🐜🪰🪲🪳🦖🦎🐍🐢🦂🕸🕷🦗🦟🦕🐙🦑🦐🦞🦀🐡🐠🐟🦓🐆🐅🐊🦭🦈🐋🐳🐬🦍🦧🦣🐘🦛🦏🐪🐫🦒🐑🐏🐖🐎🐄🐂🐃🦬🦘🦙🐐🦌🐕🐩🦮🦩🦢🦜🦚🦤🦃🐓🪶🐇🦝🦨🦡🦫🦦🦥🐁🐀🌳🌲🎄🌵🐲🐉🐾🦔🐿🌴🪵🌱🌿🍀🎍🪴🎋🪨🪸🐚🍄🪹🪺🍁🍂🍃🌾💐🌷🌹🥀🪷🌺🌸🌼🌗🌖🌕🌚🌜🌛🌝🌞🌻🌘🌑🌒🌓🌔🌙🌎🌍🌏🔥💥☄️⚡️✨🌟⭐️💫🪐🌪🌈☀️🌤⛅️🌥☁️🌦🌧💧💨🌬⛄️☃️❄️🌨🌩⛈💦🫧☔️☂️🌊🍏🍎🍐🍊🍋🍌🍉🍇🍓🍅🥝🥥🍍🥭🍑🍒🍈🫐🍆🥑🥦🥬🥒🌶🫑🌽🥕🥖🍞🥯🥐🍠🥔🧅🧄🫒🥨🧀🥚🍳🧈🥞🧇🥓🥩🥪🫓🍕🍟🍔🌭🦴🍖🍗🥙🧆🌮🌯🫔🥗🥘🫕🥫🦪🥟🍱🍣🍛🍲🍜🍝🫙🍤🍙🍚🍘🍥🥠🥮🍢🍡🍭🍮🎂🍰🧁🥧🍦🍨🍧🍬🍫🍿🍩🍪🌰🥜🫘🍯🧋🥤🧃🍵☕️🫖🍼🫗🥛🍶🍺🍻🥂🍷🥃🍸🍹🧉🧂🥢🥡🥣🍽🍴🥄🧊🍾⚽️🏀🏈⚾️🥎🎾🏐🏉🥏🪃🏏🥍🏑🏒🏸🏓🪀🎱🥅⛳️🪁🛝🏹🎣🤿🥊🥋🏂⛷🎿🥌⛸🛷🛼🛹🎽🪂🏋️‍♀️🏋️🏋️‍♂️🤼‍♀️🤼🤼‍♂️🤸‍♀️🤸🏌️‍♀️🤾‍♂️🤾🤾‍♀️🤺⛹️‍♂️⛹️⛹️‍♀️🤸‍♂️🏌️🏌️‍♂️🏇🧘‍♀️🧘🧘‍♂️🏄‍♀️🏄🏄‍♂️🚣‍♂️🚣🚣‍♀️🤽‍♂️🤽🤽‍♀️🏊‍♂️🏊🏊‍♀️🧗‍♀️🧗🧗‍♂️🚵‍♀️🚵🚵‍♂️🚴‍♀️🚴🚴‍♂️🎫🎗🏵🎖🏅🥉🥈🥇🏆🎟🎪🤹‍♀️🤹🤹‍♂️🎭🩰🎨🎬🪗🎺🎷🪘🥁🎹🎼🎧🎤🎸🪕🎻🎲♟🎯🎳🎮🎰🧩🚗🚕🚙🚌🚎🏎🚓🚑🚒🩼🦼🦽🦯🚜🚛🚚🛻🚐🛴🚲🛵🏍🛺🛞🚨🚔🚍🚝🚞🚋🚃🚟🚠🚡🚖🚘🚄🚅🚈🚂🚆🚇🚊🚉✈️🛶🚁🛸🚀🛰💺🛩🛬🛫⛵️🚤🛥🛳🚢🛟⛴⚓️🪝🗼🗽🗿🗺🚏🚥🚦🚧⛽️🏰🏯🏟🎡🎢🎠⛲️⛱🏖🛖⛺️🏕🗻🏔⛰🌋🏜🏝🏠🏡🏘🏚🏗🏭🏢🏬🏣🏛💒🏩🏫🏪🏨🏦🏥🏤⛪️🕌🕍🛕🕋⛩🛤🛣🗾🌆🌇🎆🎇🌠🌄🌅🏞🎑🏙🌃🌌🌉🌁⌚️📱📲💻⌨️🖥🖨🖱🖲📸📷📼📀💿💾💽🗜🕹📹🎥📽🎞📞☎️📟📠📺🕰⏰⏲⏱🧭🎛🎚🎙📻⌛️⏳📡🔋🪫🔌💡🔦🕯🪙💷💶💴💵💸🛢🧯🪔💰💳🪪💎⚖️🪜🧰🪛🔧🧱🪤⚙️🔩🪚⛏🛠⚒🔨⛓🧲🔫💣🧨🪓🔪🗡⚔️🧿📿🔮🏺⚱️🪦⚰️🚬🛡🪬💈⚗️🔭🔬🕳🩻🩹🩺🧹🌡🧪🧫🦠🧬🩸💉💊🪠🧺🧻🚽🚰🚿🛁🛀🧼🚪🗝🔑🛎🧴🪣🧽🪒🪥🪑🛋🛏🛌🧸🪆🖼🪞🪟🎊🪅🪄🎀🎏🎈🎁🛒🛍🎉🎎🏮🎐🪩🧧✉️📩📨📫📪🪧🏷📦📤📥💌📧📬📭📮📯📜📃📄📑🧾📇🗑📅📆🗓🗒📉📈📊🗃🗳🗄📋📂📁🗂🗞📰📖📚📙📘📗📕📒📔📓🔖🧷🔗📎🖇📐📏🧮📌✏️📝🖍🖌✒️🖋🖊✂️📍🔍🔎🔏🔐🔒🔓❤️🧡💛💚💙💜🤎🖤🤍⚪️⚫️🟤🟣🔵🟢🟡🟠🔴🟥🟧🟨🟩🟦🟫⬛️⬜️❤️‍🩹❣️❤️‍🔥💔🏿🏾🏽🏼🏻💕💞💓💗💖💘💝💟☮️☦️☯️🕎🔯✡️☸️🕉☪️✝️🛐⛎♈️♉️♊️♋️♌️♍️♎️⚛️🆔♓️♒️♑️♐️♏️🉑☢️📴📳🈶🈚️🈸🈺🈷️✴️🈲🈹🈵🈴㊗️㊙️🉐💮🆚🅰️🅱️🆎🅾️🆑🆘❌⭕️🛑🚳🚯🚷♨️💢💯🚫📛⛔️🚱🔞📵🚭❗️❕❓❔‼️🔰⚜️🔱🚸⚠️〽️🔆🔅⁉️♻️✅🈯️💹❇️✳️❎🌐💠🈳🛗🅿️♿️🚾🏧💤🌀Ⓜ️🈂️🛂🛃🛄🛅🚹🚺🚼⚧🔡🔤ℹ️🔣🈁📶🎦🚮🚻🔠🆖🆗🆙🆒🆕🆓0️⃣1️⃣🔟9️⃣8️⃣7️⃣6️⃣5️⃣4️⃣3️⃣2️⃣🔢#️⃣*️⃣⏏️▶️⏸⏯⏹⏺🔽🔼◀️⏬⏫⏪⏩⏮⏭➡️⬅️⬆️⬇️↗️↘️↙️↖️↕️🔄🔂🔁🔀⤵️⤴️↩️↪️↔️🔃🎵🎶➕➖➗✖️🟰♾️🔛🔙🔚👁‍🗨®️©️™️💱💲🔝🔜〰️➰➿✔️☑️🔘🔺▫️▪️🔲🔳🔷🔶🔹🔸🔻◾️◽️◼️◻️🔈🔇🔉🔊🔔♥️♣️♠️🗯💭💬📢📣🔕"
      ),
    };
  },
  computed: {
    visibleEmojis() {
      return this.emojis.slice(0, this.visibleCount);
    },
    visibleEmojisRight() {
      return this.emojis.slice(0, this.visibleCountRight);
    },
  },
  methods: {
    handleScroll() {
      const container = this.$refs.scrollContainer;
      if (
        container.scrollTop + container.clientHeight >=
        container.scrollHeight
      ) {
        this.visibleCount += 40; // Add 30 more items when scrolled to bottom
      }
    },
    handleScrollRight() {
      const container = this.$refs.scrollContainerRight;
      if (
        container.scrollTop + container.clientHeight >=
        container.scrollHeight
      ) {
        this.visibleCountRight += 40; // Add 30 more items when scrolled to bottom
      }
    },
    shareToTwitter() {
      const text =
        "I just combined the emojis " +
        this.one +
        " and " +
        this.two +
        ", using https://emojicombiner.com!\n\nCheck it out:";
      const url = this.combinedEmojiImage;
      const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(url)}`;

      window.open(tweetUrl, "_blank");
    },
    async copyToClipboard() {
      try {
        const response = await fetch(this.combinedEmojiImage); // Fetch the image
        const blob = await response.blob(); // Convert it to a blob

        const item = new window.ClipboardItem({ "image/png": blob });
        await navigator.clipboard.write([item]);
      } catch (error) {
        console.log("Failed to copy image: " + error);
        try {
          await navigator.clipboard.writeText(this.combinedEmojiImage);
        } catch (err) {
          alert("Failed to copy image URL to clipboard");
        }
      }
    },
    async downloadImage() {
      const response = await fetch(this.combinedEmojiImage); // Fetch the image
      const blob = await response.blob(); // Convert it to a blob
      const url = window.URL.createObjectURL(blob); // Create a URL for the blob

      const a = document.createElement("a");
      a.href = url;
      a.download = "combined-emoji.png"; // Name of the downloaded file
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setTimeout(() => {
        // Release the blob URL after a while
        window.URL.revokeObjectURL(url);
      }, 100);
    },
    combine() {
      if (this.one && this.two) {
        this.combinedEmojiImage = getEmojiMixUrl(this.one, this.two);
      }
    },
    setEmojiOne(emoji) {
      this.one = emoji;
      this.combine();
    },
    setEmojiTwo(emoji) {
      this.two = emoji;
      this.combine();
    },
  },
};
</script>

<style>
h1 {
  font-family: "Fira Sans", sans-serif;
}
</style>